import { useEffect, useRef } from "react";

/**
 * How long the bar stays up after the last scroll event. Long enough to follow
 * a flick to the bottom of a menu, short enough not to read as permanent.
 */
const IDLE_DELAY_MS = 700;

/** Paired with the `.scrollbar-fade` utility in `index.css`. */
const SCROLLING_CLASS = "is-scrolling";

/**
 * Shows a scrollable element's scrollbar only while it is being scrolled.
 *
 * The class is toggled on the node directly rather than through state: a
 * re-render on every scroll event would cost far more than the bar is worth,
 * and re-rendering a menu mid-scroll is what makes the display flicker. The
 * `.scrollbar-fade` utility reserves the gutter permanently and animates only
 * the thumb's colour, so nothing beside it moves when the bar appears.
 *
 * Attach the returned ref to the element that scrolls, and give it
 * `className="scrollbar-fade"`.
 */
export const useScrollFade = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let idleTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleScroll = () => {
      node.classList.add(SCROLLING_CLASS);

      clearTimeout(idleTimeoutId);
      idleTimeoutId = setTimeout(
        () => node.classList.remove(SCROLLING_CLASS),
        IDLE_DELAY_MS,
      );
    };

    // Passive: this listener never calls `preventDefault`, and saying so keeps
    // it off the critical path of the scroll itself.
    node.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearTimeout(idleTimeoutId);
      node.removeEventListener("scroll", handleScroll);
      node.classList.remove(SCROLLING_CLASS);
    };
  }, []);

  return ref;
};
